import React, { Suspense, lazy } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import './index.css';
import ProtectedRoute from './components/ProtectedRoute';
import { getStoredUser } from './services';
// The marketing page at "/" stays eagerly imported — it is the entry point for cold visitors and
// must paint from the entry bundle alone. EVERYTHING else is lazy: before this split, the single
// CRA bundle shipped the whole product (workspace, admin console, templates…) to every marketing
// visitor — 1.27 MB parsed — which pushed onload late enough on phones that Clarity measured ~7 s.
import Landing from './marketing/Landing';
const SignUp = lazy(() => import('./pages/SignUp'));
const Login = lazy(() => import('./pages/Login'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const LandingScreen = lazy(() => import('./pages/LandingScreen'));
const Membership = lazy(() => import('./pages/Membership'));
const Payment = lazy(() => import('./pages/Payment'));
const Security = lazy(() => import('./pages/Security'));
const CaseHistory = lazy(() => import('./pages/CaseHistory'));
const PricingPlans = lazy(() => import('./pages/PricingPlans'));
const AddTemplate = lazy(() => import('./pages/AddTemplate'));
const Templates = lazy(() => import('./pages/Templates'));
const AdminConsole = lazy(() => import('./pages/AdminConsole'));
const PageNotFound = lazy(() => import('./pages/PageNotFound'));

// The real product workspace. Logged-in users get the full app; visitors arriving via the marketing
// "Try now" CTA (/dashboard?trial=1) get the SAME screen in no-login guest mode (LandingScreen reads
// `guest` and swaps to the per-IP trial endpoint + sign-up prompts). Everyone else -> /login.
function DashboardRoute() {
  const location = useLocation();
  if (getStoredUser()) return <LandingScreen />;
  if (new URLSearchParams(location.search).get('trial') === '1') return <LandingScreen guest />;
  return <Navigate to="/login" state={{ from: location }} replace />;
}

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

root.render(
  <React.StrictMode>
    <BrowserRouter>
      <Toaster
        position="top-center"
        toastOptions={{
          duration: 4000,
          className: 'font-inter',
          style: {
            background: '#181818',
            color: '#DBE0FA',
            border: '1px solid #333333',
          },
        }}
      />
      {/* null fallback: lazy chunks resolve in well under a second on any route change, and a
          flash of spinner would be worse than a brief blank. "/" never suspends (eager import). */}
      <Suspense fallback={null}>
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/signup" element={<SignUp />} />
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/dashboard" element={<DashboardRoute />} />
        <Route path="/membership" element={<ProtectedRoute><Membership /></ProtectedRoute>} />
        <Route path="/payment" element={<ProtectedRoute><Payment /></ProtectedRoute>} />
        <Route path="/security" element={<ProtectedRoute><Security /></ProtectedRoute>} />
        <Route path="/case-history" element={<ProtectedRoute><CaseHistory /></ProtectedRoute>} />
        <Route path="/pricing" element={<ProtectedRoute><PricingPlans /></ProtectedRoute>} />
        <Route path="/add-template" element={<ProtectedRoute><AddTemplate /></ProtectedRoute>} />
        <Route path="/templates" element={<ProtectedRoute><Templates /></ProtectedRoute>} />
        {/* /admin: native DXR analytics dashboard. Self-gated by its own admin login
            (mirrors /outreach) — NOT wrapped in ProtectedRoute, which checks for a
            PRODUCT user; admins authenticate via /api/v1/admin/login instead. */}
        <Route path="/admin" element={<AdminConsole initial="admin" />} />
        {/* /outreach: same console, opens on the Outreach tab */}
        <Route path="/outreach" element={<AdminConsole initial="outreach" />} />
        <Route path="*" element={<PageNotFound />} />
      </Routes>
      </Suspense>
    </BrowserRouter>
  </React.StrictMode>
);
